import { BusinessSubscription } from "../../../interfaces/AdminSubscriptionObject";
import { coerceSingle } from "../../helpers/ApiEnvelopeHelper";

// GET /api/v1/business-subscriptions/list -> BusinessSubscription[].
// Null-guards and coerces only; the payload is already camelCase.

const toStr = (value: unknown): string =>
  value === undefined || value === null ? "" : String(value);

/** Dates and gateway ids are legitimately null on the wire (an active
 *  subscription has no cancelledAt), so absent stays null rather than "". */
const toNullable = (value: unknown): string | null =>
  value === undefined || value === null || value === "" ? null : String(value);

const toCount = (value: unknown): number => Number(value ?? 0) || 0;

/** Null (not 0) when absent: a missing amount must render as "-", never as
 *  a confident ₹0. Mirrors SubscriptionPlan.amountPaise's convention. */
const toPaise = (value: unknown): number | null =>
  typeof value === "number" ? value : null;

export function convertBusinessSubscription(
  raw: Record<string, unknown>
): BusinessSubscription {
  return {
    subscriptionId: toStr(raw?.subscriptionId),
    businessId: toStr(raw?.businessId),
    planId: toStr(raw?.planId),
    billingInterval: toStr(raw?.billingInterval),
    gatewaySubscriptionId: toNullable(raw?.gatewaySubscriptionId),
    gatewayCustomerId: toNullable(raw?.gatewayCustomerId),
    shortUrl: toNullable(raw?.shortUrl),
    status: toStr(raw?.status),
    subscriptionStart: toNullable(raw?.subscriptionStart),
    subscriptionEnd: toNullable(raw?.subscriptionEnd),
    chargeAt: toNullable(raw?.chargeAt),
    endedAt: toNullable(raw?.endedAt),
    paidCount: toCount(raw?.paidCount),
    remainingCount: toCount(raw?.remainingCount),
    totalCount: toCount(raw?.totalCount),
    amountPaise: toPaise(raw?.amountPaise),
    currency: toStr(raw?.currency),
    createdAt: toNullable(raw?.createdAt),
    updatedAt: toNullable(raw?.updatedAt),
    // Fail-closed: only an explicit true means "will cancel" / "trial taken".
    cancelAtCycleEnd: raw?.cancelAtCycleEnd === true,
    cancelledAt: toNullable(raw?.cancelledAt),
    gateway: toStr(raw?.gateway),
    cashfreeSessionId: toNullable(raw?.cashfreeSessionId),
    cashfreeSessionExpiry: toNullable(raw?.cashfreeSessionExpiry),
    trialReserved: raw?.trialReserved === true,
    trialConsumedAt: toNullable(raw?.trialConsumedAt),
    mandateStart: toNullable(raw?.mandateStart),
    mandateEnd: toNullable(raw?.mandateEnd),
  };
}

/**
 * contents -> BusinessSubscription[]. The observed shape is a flat array of
 * rows; the wrapper branch is a cheap guard against this service adopting the
 * same wrapping the onboarding endpoints use. `subscriptionId` separates a row
 * from a wrapper.
 */
export function convertBusinessSubscriptionList(
  contents: unknown
): BusinessSubscription[] {
  if (Array.isArray(contents)) {
    const first = contents[0] as Record<string, unknown> | undefined;
    if (!first || first.subscriptionId !== undefined) {
      return contents.map((row) =>
        convertBusinessSubscription(row as Record<string, unknown>)
      );
    }
  }
  const wrapper = coerceSingle(contents);
  const rows = wrapper.subscriptions ?? wrapper.businessSubscriptions;
  if (!Array.isArray(rows)) return [];
  return rows.map((row) =>
    convertBusinessSubscription(row as Record<string, unknown>)
  );
}
